import { FightGameApi } from '@/api/backendService';
import type {
  FightEndTurnRequest,
  FightEndVoteResult,
  FightGeneric,
  Fighter,
  FightInvitation,
  FightSide,
  FightState,
  FightTurnResult,
  OpenaiCharacter,
  TrainingSession,
} from '@/api/dx-backend';

type FightEventBus = {
  on: (event: string, handler: (data: any) => void) => void;
  off: (event: string, handler: (data: any) => void) => void;
};

const CLOCK_EVENTS = {
  ROUND_ADVANCED: 'character::fight_round_advanced',
  TURN_ENDED: 'fight::fight_turn_ended',
  FIGHT_ENDED: 'fight::fight_ended',
  END_VOTE: 'fight::fight_end_vote',
  SIDE_CREATED: 'fight::fight_side_created',
  SIDE_UPDATED: 'fight::fight_side_updated',
  PARTICIPANT_SIDE_CHANGED: 'fight::fight_participant_side_changed',
} as const;

/**
 * Fight Service for managing character fights
 * This service provides methods to:
 * - Check if a character is in a fight
 * - Get information about a fight
 * - Check if a character is pending to join a fight
 * - Get other participants in a fight
 *
 * It also carries the fight's own clock: once `attach()`ed to a WebSocket
 * event bus it keeps the observed `(round, phase)` and the deadline the
 * server last quoted in sync, exposes `endTurn()`/`forfeit()`, and emits
 * `roundAdvanced` / `turnEnded` / `fightEnded` / `turnConflict` so a HUD
 * component can subscribe without touching the generated API client itself.
 */
export class FightService {
  private currentFight: FightGeneric | null = null;
  private pendingJoinUsers: Fighter[] = [];
  private joinedUsers: Fighter[] = [];

  private bus: FightEventBus | null = null;
  private characterId: string | null = null;
  private eventListeners: Map<string, Array<(data: any) => void>> = new Map();

  private clockState: FightState | null = null;
  private skewMs = 0;

  /**
   * The API client is resolved on first use, never at module evaluation, and
   * can be overridden by tests. `FightService` sits inside the app's import
   * graph (backendService -> store -> ... -> services), so reading
   * `FightGameApi` at module-evaluation time can hit its temporal dead zone.
   */
  private readonly injectedApi: typeof FightGameApi | null;

  constructor(api: typeof FightGameApi | null = null) {
    this.injectedApi = api;
  }

  private get api(): typeof FightGameApi {
    return this.injectedApi || FightGameApi;
  }

  private readonly onRoundAdvanced = (data: any) => this.handleRoundAdvanced(data);
  private readonly onTurnEnded = (data: any) => this.handleTurnEnded(data);
  private readonly onFightEnded = (data: any) => this.handleFightEnded(data);
  private readonly onEndVote = (data: any) => this.handleEndVote(data);
  private readonly onSideCreated = (data: any) => this.handleSideCreated(data);
  private readonly onSideUpdated = (data: any) => this.handleSideUpdated(data);
  private readonly onParticipantSideChanged = (data: any) => this.handleParticipantSideChanged(data);

  /**
   * The clock's last known state (round, phase, deadline, participants...).
   */
  public get state(): FightState | null {
    return this.clockState;
  }

  /**
   * Milliseconds to add to `Date.now()` to correct for server/client clock
   * drift. Recomputed from `server_time` on every REST read and WS payload.
   */
  public get currentSkewMs(): number {
    return this.skewMs;
  }

  /**
   * Subscribe the clock to a character's fight events on the given bus.
   * Safe to call again with a different character — the previous
   * subscription is torn down first.
   */
  public attach(bus: FightEventBus, characterId: string): void {
    this.unsubscribe();
    this.bus = bus;
    this.characterId = characterId;
    this.bus.on(CLOCK_EVENTS.ROUND_ADVANCED, this.onRoundAdvanced);
    this.bus.on(CLOCK_EVENTS.TURN_ENDED, this.onTurnEnded);
    this.bus.on(CLOCK_EVENTS.FIGHT_ENDED, this.onFightEnded);
    this.bus.on(CLOCK_EVENTS.END_VOTE, this.onEndVote);
    this.bus.on(CLOCK_EVENTS.SIDE_CREATED, this.onSideCreated);
    this.bus.on(CLOCK_EVENTS.SIDE_UPDATED, this.onSideUpdated);
    this.bus.on(CLOCK_EVENTS.PARTICIPANT_SIDE_CHANGED, this.onParticipantSideChanged);
  }

  /**
   * Unsubscribe from the event bus and drop the cached clock state. Use this
   * when the character actually leaves the fight (or the component owning
   * the service unmounts) — a mere re-`attach()` (e.g. after a character
   * switch mid-session) does not wipe state it hasn't replaced yet.
   */
  public detach(): void {
    this.unsubscribe();
    this.characterId = null;
    this.clockState = null;
    this.skewMs = 0;
  }

  private unsubscribe(): void {
    if (this.bus) {
      this.bus.off(CLOCK_EVENTS.ROUND_ADVANCED, this.onRoundAdvanced);
      this.bus.off(CLOCK_EVENTS.TURN_ENDED, this.onTurnEnded);
      this.bus.off(CLOCK_EVENTS.FIGHT_ENDED, this.onFightEnded);
      this.bus.off(CLOCK_EVENTS.END_VOTE, this.onEndVote);
      this.bus.off(CLOCK_EVENTS.SIDE_CREATED, this.onSideCreated);
      this.bus.off(CLOCK_EVENTS.SIDE_UPDATED, this.onSideUpdated);
      this.bus.off(CLOCK_EVENTS.PARTICIPANT_SIDE_CHANGED, this.onParticipantSideChanged);
    }
    this.bus = null;
  }

  /**
   * Fetch the requesting character's current fight clock and start tracking
   * it. Throws (does not swallow) so a caller can distinguish "not in a
   * fight" (404) from a real failure.
   */
  public async loadCurrentClock(): Promise<FightState> {
    const response = await this.api.fightFightCurrentRetrieve();
    this.applyState(response.data);
    return response.data;
  }

  /**
   * Fetch a specific fight's clock and start tracking it.
   */
  public async loadClock(fightId: string): Promise<FightState> {
    const response = await this.api.fightFightStateRetrieve(fightId);
    this.applyState(response.data);
    return response.data;
  }

  /**
   * End the requesting character's turn for the `(round, phase)` the client
   * last observed. On a 409 the server's current state is applied locally
   * and a `turnConflict` event is emitted instead of throwing, so a caller
   * does not have to special-case the race to resolve it.
   */
  public async endTurn(character: OpenaiCharacter): Promise<FightTurnResult | null> {
    if (!this.clockState) {
      return null;
    }
    const fightId = this.clockState.fight_id;
    const body: FightEndTurnRequest = {
      round: this.clockState.round,
      phase: this.clockState.phase as FightEndTurnRequest['phase'],
    };
    try {
      const response = await this.api.fightFightEndTurnCreate(fightId, body);
      this.applyState(response.data.state);
      this.emit('turnResolved', response.data);
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 409 && error.response.data) {
        this.applyState(error.response.data as FightState);
        this.emit('turnConflict', this.clockState);
        return null;
      }
      throw error;
    }
  }

  /**
   * Surrender the fight the clock is tracking. Always available, whatever
   * the phase or deadline. Unlike the old forfeit this does not remove the
   * character from the fight — it goes into a coma, stays on the board, and
   * can be robbed. `forfeit` is kept server-side as a compatibility alias
   * for the same route; this client only ever calls the `surrender` one.
   */
  public async surrender(): Promise<FightState | null> {
    if (!this.clockState) {
      return null;
    }
    const response = await this.api.fightFightSurrenderCreate(this.clockState.fight_id);
    this.applyState(response.data);
    return response.data;
  }

  /**
   * Pick an already-existing side while pending, per `FightSideChoiceService.
   * choose_existing`. Refused server-side once the caller is assigned or the
   * pending window has closed — the donut is gone by then anyway, since
   * `may_choose_side` flips false on the next state it reads.
   */
  public async chooseSide(sideId: string): Promise<FightState | null> {
    if (!this.clockState) {
      return null;
    }
    const response = await this.api.fightFightChooseSideCreate(this.clockState.fight_id, {side_id: sideId});
    this.applyState(response.data);
    return response.data;
  }

  /**
   * Create a new side and pick it in one call, per `FightSideChoiceService.
   * create_and_choose`. `name` is optional — the server defaults it to the
   * caller's organization, or their own name if they have none — so the
   * donut's "new side" affordance needs no text input of its own.
   */
  public async createSide(name?: string): Promise<FightState | null> {
    if (!this.clockState) {
      return null;
    }
    const response = await this.api.fightFightCreateSideCreate(
        this.clockState.fight_id,
        name ? {name} : undefined,
    );
    this.applyState(response.data);
    return response.data;
  }

  /**
   * Ask for the fight to be over, or take that back with `agree = false`.
   *
   * A knockout no longer closes a fight, so this is how one that has run its
   * course actually stops. It passes only once every *standing* participant
   * agrees; a downed participant has no vote and is not counted, which is why
   * the server answers 403 rather than 200 when a downed character calls it.
   * The returned state replaces the clock's own, so the tally the rail draws
   * is always the one the server just computed.
   */
  public async voteToEnd(agree = true): Promise<FightEndVoteResult | null> {
    if (!this.clockState) {
      return null;
    }
    const response = await this.api.fightFightEndVoteCreate(this.clockState.fight_id, {agree});
    this.applyState(response.data.state);
    this.emit('endVoteChanged', response.data);
    return response.data;
  }

  /**
   * Whether the given character's vote is currently standing, as the clock
   * last saw it. Reads the participant row rather than a cached flag of its
   * own: a round advance clears the tally server-side, and the clock's reset
   * in `handleRoundAdvanced` is then the only place that has to know it.
   */
  public hasVotedToEnd(characterId: string | null | undefined): boolean {
    if (!characterId || !this.clockState) {
      return false;
    }
    return (this.clockState.end_votes || []).some(id => id === characterId);
  }

  /**
   * Omitting `characterId` trains whichever character the client is currently
   * playing; the dashboard names one explicitly, since it is not necessarily
   * the character the client is playing right now.
   */
  public async startOrResumeTraining(characterId?: string): Promise<TrainingSession> {
    const response = await this.api.fightTrainingStartCreate(
        characterId ? {character: characterId} : undefined,
    );
    return response.data;
  }

  /**
   * The open training of one of the caller's characters, or null when it has
   * none. `characterId` is the **real** character, never the clone: a session
   * is keyed on the character that spawned it.
   */
  public async getCurrentTraining(characterId?: string): Promise<TrainingSession | null> {
    const response = await this.api.fightTrainingCurrentRetrieve(characterId);
    if (response.status === 204 || !response.data) {
      return null;
    }
    return response.data;
  }

  public async endTraining(characterId?: string): Promise<void> {
    await this.api.fightTrainingEndCreate(characterId ? {character: characterId} : undefined);
  }

  /**
   * Duel challenges the requesting character sent or received. Read-only:
   * answering one is an ordinary `ACCEPT`/`REJECT_DUEL_INVITATION` character
   * action through the action pipeline, not a fight endpoint.
   */
  public async listInvitations(): Promise<FightInvitation[]> {
    const response = await this.api.fightFightInvitationsList();
    return response.data || [];
  }

  public on(event: string, callback: (data: any) => void): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }

  public off(event: string, callback: (data: any) => void): void {
    const listeners = this.eventListeners.get(event);
    if (!listeners) {
      return;
    }
    const index = listeners.indexOf(callback);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  }

  private emit(event: string, data: any): void {
    const listeners = this.eventListeners.get(event);
    if (!listeners) {
      return;
    }
    listeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in FightService listener for ${event}:`, error);
      }
    });
  }

  private applyState(state: FightState): void {
    this.clockState = state;
    this.recomputeSkew(state.server_time);
  }

  private recomputeSkew(serverTime: string): void {
    const serverMs = new Date(serverTime).getTime();
    if (!Number.isNaN(serverMs)) {
      this.skewMs = serverMs - Date.now();
    }
  }

  private handleRoundAdvanced(data: any): void {
    if (!this.isForTrackedFight(data)) {
      return;
    }
    if (data.character_id && this.characterId && data.character_id !== this.characterId) {
      return;
    }
    this.recomputeSkew(data.server_time);
    if (this.clockState) {
      this.clockState = {
        ...this.clockState,
        round: data.round,
        phase: data.phase,
        phase_started_at: data.phase_started_at ?? null,
        phase_ends_at: data.phase_ends_at ?? null,
        phase_timeout_seconds: data.phase_timeout_seconds,
        server_time: data.server_time,
        tactical_map: data.tactical_map ?? null,
        /* A freshly opened phase carries no acknowledgements, and the event
           says nothing about them. Without this reset the previous phase's
           tally survives, the rail reads "resolving" forever and the
           countdown never comes back. */
        ended_turns: [],
        ended_turn_count: 0,
        /* A vote is about *this* round and the server only ever counts rows
           for the round it is on, so a tally carried across the boundary is
           not stale data — it is a different question's answer. */
        end_vote_round: data.round,
        end_votes: [],
        end_vote_count: 0,
        participants: (this.clockState.participants || []).map(
            (participant: any) => ({
              ...participant,
              ended_turn: false,
              end_vote: false,
              breakthrough_used: false,
            }),
        ),
      };
    }
    this.emit('roundAdvanced', data);
  }

  /**
   * Somebody voted, or withdrew. The event carries the tally but not who is
   * in it, so the counts are taken from the event and the one voter's own row
   * is moved to match — enough for a holdout to see that they are the one
   * being waited for, without a read back to the server.
   */
  private handleEndVote(data: any): void {
    if (!this.isForTrackedFight(data)) {
      return;
    }
    if (this.clockState && data.round === this.clockState.round) {
      const voter = data.character_id;
      const voted = (this.clockState.end_votes || []).filter(id => id !== voter);
      if (data.agreed) {
        voted.push(voter);
      }
      this.clockState = {
        ...this.clockState,
        end_vote_round: data.round,
        end_votes: voted,
        end_vote_count: data.voted_count,
        end_vote_required: data.required_count,
        participants: (this.clockState.participants || []).map(
            (participant: any) => participant.character_id === voter
                ? {...participant, end_vote: !!data.agreed}
                : participant,
        ),
      };
    }
    this.emit('endVoteChanged', data);
  }

  /**
   * An ack can move the deadline without opening a phase: once the last
   * chooser has picked, the server pulls establishment's deadline in to its
   * three-second floor. Nothing else announces that — `fight_round_advanced`
   * only fires when a phase actually opens — so a client that kept counting
   * to the 120 s it was handed at open sat on the side-choice screen for the
   * remainder of it before its own countdown nudged the phase along.
   *
   * Applied only while the event still describes the phase on screen. The
   * server withholds the deadline when the ack advanced the phase, and this
   * guard covers the rest: a turn-ended that arrives after its phase closed
   * must not write the old phase's timing over the new one.
   */
  private handleTurnEnded(data: any): void {
    if (!this.isForTrackedFight(data)) {
      return;
    }
    if (data.server_time) {
      this.recomputeSkew(data.server_time);
    }
    if (this.clockState) {
      const forCurrentPhase = data.round === this.clockState.round
          && data.phase === this.clockState.phase;
      this.clockState = {
        ...this.clockState,
        ended_turn_count: data.ended_turn_count,
        participant_count: data.participant_count,
        ...(forCurrentPhase && data.phase_ends_at
            ? {phase_ends_at: data.phase_ends_at, server_time: data.server_time}
            : {}),
      };
    }
    this.emit('turnEnded', data);
  }

  private handleFightEnded(data: any): void {
    if (!this.isForTrackedFight(data)) {
      return;
    }
    if (this.clockState) {
      this.clockState = {
        ...this.clockState,
        outcome: data.outcome ?? null,
        winner_id: data.winner_id ?? null,
      };
    }
    this.emit('fightEnded', data);
  }

  /**
   * A side was created, mid-fight (the GM or the player's own choose/create
   * routes). Replaces the side by id if it is already known, otherwise
   * appends it — a spectator who was not present for the creation still
   * sees it show up.
   */
  private handleSideCreated(data: any): void {
    if (!this.isForTrackedFight(data)) {
      return;
    }
    this.upsertSide(data.side);
    this.emit('sideCreated', data);
  }

  /**
   * A side was renamed, or its membership/power shape changed. Same upsert
   * as creation — the donut and the side rail must never do their own
   * arithmetic on `power_share`, so the whole side object is replaced.
   */
  private handleSideUpdated(data: any): void {
    if (!this.isForTrackedFight(data)) {
      return;
    }
    this.upsertSide(data.side);
    this.emit('sideUpdated', data);
  }

  private upsertSide(side: FightSide): void {
    if (!this.clockState || !side) {
      return;
    }
    const sides = this.clockState.sides || [];
    const index = sides.findIndex(existing => existing.id === side.id);
    const nextSides = index === -1
        ? [...sides, side]
        : sides.map((existing, i) => (i === index ? side : existing));
    this.clockState = {...this.clockState, sides: nextSides};
  }

  /**
   * A participant moved to a different side, by their own choice or the
   * GM's hand. Only `side_id` changes on the participant row; the sides
   * themselves react to their own `fight_side_updated` events for their
   * member counts and power shares.
   */
  private handleParticipantSideChanged(data: any): void {
    if (!this.isForTrackedFight(data)) {
      return;
    }
    if (this.clockState) {
      this.clockState = {
        ...this.clockState,
        participants: (this.clockState.participants || []).map(
            (participant: any) => participant.character_id === data.character_id
                ? {...participant, side_id: data.to_side_id ?? null}
                : participant,
        ),
      };
    }
    this.emit('participantSideChanged', data);
  }

  private isForTrackedFight(data: any): boolean {
    return !!this.clockState && data?.fight_id === this.clockState.fight_id;
  }

  /**
   * Determines if a character is in a fight by checking the fight property
   * @param character The OpenaiCharacter to check
   * @returns Promise<boolean> indicating if the character is in a fight
   */
  public async isInFight(character: OpenaiCharacter): Promise<boolean> {
    // Check if the character has a fight UUID
    const hasFight = !!character.fight;

    // If the character doesn't have a fight UUID, clean the current fight data
    if (!hasFight) {
      this.currentFight = null;
      this.pendingJoinUsers = [];
      this.joinedUsers = [];
    }

    return hasFight;
  }

  /**
   * Determines if a character is pending to join a fight
   * @param character The OpenaiCharacter to check
   * @returns Promise<boolean> indicating if the character is pending to join a fight
   */
  public async isPendingJoin(character: OpenaiCharacter): Promise<boolean> {
    // Use cached fight data if available
    if (!character.fight) {
      return false;
    }

    // Get fight data (uses cache if available)
    await this.getFight(character);

    if (!this.currentFight) {
      return false;
    }

    return this.currentFight.pending_join?.some(fighter => fighter.id === character.id) || false;
  }

  /**
   * Gets the current fight for a character
   * Uses cached fight data if available, otherwise initializes it
   * @param character The OpenaiCharacter to get the fight for
   * @returns Promise<FightGeneric | null> The fight or null if not in a fight
   */
  public async getFight(character: OpenaiCharacter): Promise<FightGeneric | null> {
    // Clean fight data if character doesn't have a fight UUID
    if (!character.fight) {
      this.currentFight = null;
      this.pendingJoinUsers = [];
      this.joinedUsers = [];
      return null;
    }

    // If we already have fight data and it matches the character's fight UUID, use the cached data
    if (this.currentFight && this.currentFight.id === character.fight) {
      return this.currentFight;
    }

    // If we don't have fight data or it doesn't match, fetch it
    return this.refreshFight(character);
  }

  /**
   * Refreshes the fight data from the API
   * @param character The OpenaiCharacter to refresh the fight for
   * @returns Promise<FightGeneric | null> The refreshed fight or null if not in a fight or error
   */
  public async refreshFight(character: OpenaiCharacter): Promise<FightGeneric | null> {
    // Clean fight data if character doesn't have a fight UUID
    if (!character.fight) {
      this.currentFight = null;
      this.pendingJoinUsers = [];
      this.joinedUsers = [];
      return null;
    }

    // Fetch the latest fight data from the API
    try {
      const response = await this.api.fightFightRetrieve(character.fight);
      this.currentFight = response.data;
      this.pendingJoinUsers = this.currentFight.pending_join || [];
      this.joinedUsers = this.currentFight.joined || [];
      return this.currentFight;
    } catch (error) {
      /* A character can carry a `fight` id the server will not hand back — a
         fight that has been closed or removed, or one outside the campaign the
         GM just switched into. That is "no fight", not a failure, and logging
         it as an error spammed the console on every character switch. */
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 403) {
        console.debug('[PlayerFight] No readable fight for this character:', character.fight);
      } else {
        console.error('Error fetching fight:', error);
      }
      // Clean fight data if there was an error fetching the fight
      this.currentFight = null;
      this.pendingJoinUsers = [];
      this.joinedUsers = [];
      return null;
    }
  }

  /**
   * Gets the participants in a fight
   * @param character The OpenaiCharacter to get the fight participants for
   * @returns Promise<Fighter[]> Array of fight participants
   */
  public async getFightParticipants(character: OpenaiCharacter): Promise<Fighter[]> {
    // Use cached fight data if available
    if (!character.fight) {
      return [];
    }

    // Get fight data (uses cache if available)
    await this.getFight(character);

    if (!this.currentFight) {
      return [];
    }

    return this.joinedUsers;
  }

  /**
   * Gets the pending join users in a fight
   * @param character The OpenaiCharacter to get the pending join users for
   * @returns Promise<Fighter[]> Array of pending join fighters
   */
  public async getPendingJoinUsers(character: OpenaiCharacter): Promise<Fighter[]> {
    // Use cached fight data if available
    if (!character.fight) {
      return [];
    }

    // Get fight data (uses cache if available)
    await this.getFight(character);

    if (!this.currentFight) {
      return [];
    }

    return this.pendingJoinUsers;
  }

  /**
   * Gets the attacker in a fight
   * @param character The OpenaiCharacter to get the attacker for
   * @returns Promise<Fighter | null> The attacker or null if not in a fight
   */
  public async getAttacker(character: OpenaiCharacter): Promise<Fighter | null> {
    // Use cached fight data if available
    if (!character.fight) {
      return null;
    }

    // Get fight data (uses cache if available)
    await this.getFight(character);

    if (!this.currentFight) {
      return null;
    }

    return this.currentFight.attacker;
  }

  /**
   * Gets the defender in a fight
   * @param character The OpenaiCharacter to get the defender for
   * @returns Promise<Fighter | null> The defender or null if not in a fight
   */
  public async getDefender(character: OpenaiCharacter): Promise<Fighter | null> {
    // Use cached fight data if available
    if (!character.fight) {
      return null;
    }

    // Get fight data (uses cache if available)
    await this.getFight(character);

    if (!this.currentFight) {
      return null;
    }

    return this.currentFight.defender;
  }

  /**
   * Checks if the fight is a duel
   * @param character The OpenaiCharacter to check
   * @returns Promise<boolean | null> indicating if the fight is a duel or null if not in a fight
   */
  public async isDuel(character: OpenaiCharacter): Promise<boolean | null> {
    // Use cached fight data if available
    if (!character.fight) {
      return null;
    }

    // Get fight data (uses cache if available)
    await this.getFight(character);

    if (!this.currentFight) {
      return null;
    }

    return this.currentFight.duel || false;
  }

  /**
   * Checks if the fight is open for others to join
   * @param character The OpenaiCharacter to check
   * @returns Promise<boolean | null> indicating if the fight is open or null if not in a fight
   */
  public async isOpen(character: OpenaiCharacter): Promise<boolean | null> {
    // Use cached fight data if available
    if (!character.fight) {
      return null;
    }

    // Get fight data (uses cache if available)
    await this.getFight(character);

    if (!this.currentFight) {
      return null;
    }

    return this.currentFight.open || false;
  }

  /**
   * Gets the current round of the fight
   * @param character The OpenaiCharacter to get the current round for
   * @returns Promise<number | null> The current round or null if not in a fight
   */
  public async getCurrentRound(character: OpenaiCharacter): Promise<number | null> {
    // Use cached fight data if available
    if (!character.fight) {
      return null;
    }

    // Get fight data (uses cache if available)
    await this.getFight(character);

    if (!this.currentFight) {
      return null;
    }

    return this.currentFight.current_round || 0;
  }

  /**
   * Gets the position ID where the fight is taking place
   * @param character The OpenaiCharacter to get the position for
   * @returns Promise<string | null> The position ID or null if not in a fight
   */
  public async getPosition(character: OpenaiCharacter): Promise<string | null> {
    // Use cached fight data if available
    if (!character.fight) {
      return null;
    }

    // Get fight data (uses cache if available)
    await this.getFight(character);

    if (!this.currentFight) {
      return null;
    }

    return this.currentFight.position;
  }

  /**
   * Gets the display name of the location where the fight is taking place
   * @param character The OpenaiCharacter to get the location name for
   * @returns Promise<string | null> The location name or null if not in a fight
   */
  public async getPositionName(character: OpenaiCharacter): Promise<string | null> {
    if (!character.fight) {
      return null;
    }

    await this.getFight(character);

    if (!this.currentFight) {
      return null;
    }

    return this.currentFight.position_name || null;
  }

  /**
   * Gets the campaign ID the fight belongs to
   * @param character The OpenaiCharacter to get the campaign for
   * @returns Promise<string | null> The campaign ID or null if not in a fight
   */
  public async getCampaign(character: OpenaiCharacter): Promise<string | null> {
    // Use cached fight data if available
    if (!character.fight) {
      return null;
    }

    // Get fight data (uses cache if available)
    await this.getFight(character);

    if (!this.currentFight) {
      return null;
    }

    return this.currentFight.campaign;
  }

  /**
   * Gets the timestamp when the fight was created
   * @param character The OpenaiCharacter to get the created timestamp for
   * @returns Promise<number | null> The created timestamp or null if not in a fight or not available
   */
  public async getCreatedTimestamp(character: OpenaiCharacter): Promise<number | null> {
    // Use cached fight data if available
    if (!character.fight) {
      return null;
    }

    // Get fight data (uses cache if available)
    await this.getFight(character);

    if (!this.currentFight) {
      return null;
    }

    return this.currentFight.created || null;
  }

  /**
   * Gets the timestamp when the fight ended
   * @param character The OpenaiCharacter to get the ended timestamp for
   * @returns Promise<number | null> The ended timestamp or null if not in a fight or not ended
   */
  public async getEndedTimestamp(character: OpenaiCharacter): Promise<number | null> {
    // Use cached fight data if available
    if (!character.fight) {
      return null;
    }

    // Get fight data (uses cache if available)
    await this.getFight(character);

    if (!this.currentFight) {
      return null;
    }

    return this.currentFight.ended_at || null;
  }

  /**
   * Checks if the fight has ended
   * @param character The OpenaiCharacter to check
   * @returns Promise<boolean> indicating if the fight has ended
   */
  public async hasEnded(character: OpenaiCharacter): Promise<boolean> {
    // Use cached fight data if available
    if (!character.fight) {
      return false;
    }

    // Get fight data (uses cache if available)
    await this.getFight(character);

    if (!this.currentFight) {
      return false;
    }

    return !!this.currentFight.ended_at;
  }
}

// Export a singleton instance of the FightService
export const fightService = new FightService();